import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Grid, Modal, Typography, TextField, Stack, Select, MenuItem } from "@mui/material";

import { Button, Alert } from "../../../common/styled";
import ModalBox from "../../../../common/ModalBox";

import useLLMConfig from "../../../../../hooks/user/useLLMConfig";

const MatchTypes = ['sentence', 'full'];

function ToxicityModal({ open, handleClose, zoneUid }) {
  const { llmOutputScanners, configLLMSettingOutputScanners } = useLLMConfig();

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        match_type: llmOutputScanners ? llmOutputScanners?.toxicity?.match_type : "",
        threshold: llmOutputScanners ? llmOutputScanners?.toxicity?.threshold : 0,
      }}
      validationSchema={Yup.object().shape({
        match_type: Yup.string().nullable().required("Match type is required"),
        threshold: Yup.number().min(0, "Threshold must be at least 0").max(1, "Threshold must be at most 1"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          values.submit = undefined;
          await configLLMSettingOutputScanners(zoneUid, "toxicity", values);
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, values }) => (
        <React.Fragment>
          <Modal
            open={open}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
          >
            <ModalBox sx={{ width: "60vw" }} title="Edit Toxicity Scanner" handleClose={handleClose}>
              <form noValidate onSubmit={handleSubmit}>
                <Grid container p={4} spacing={3}>
                  <Grid item xs={12}>
                    {errors.submit && (
                      <Alert mt={2} mb={3} variant="outlined" severity="error">
                        {errors.submit}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Threshold</Typography>
                      <TextField
                        fullWidth
                        name="threshold"
                        type="number"
                        error={Boolean(touched.threshold && errors.threshold)}
                        helperText={touched.threshold && errors.threshold}
                        value={values.threshold}
                        onBlur={(e) => {
                          if (e.target.value === "") setFieldValue("threshold", 0);
                        }}
                        onChange={handleChange}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Match Type</Typography>
                      <Select
                        fullWidth
                        name="match_type"
                        value={values.match_type}
                        error={Boolean(touched.match_type && errors.match_type)}
                        helperText={touched.match_type && errors.match_type}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      >
                        {MatchTypes.map((type) => (
                          <MenuItem value={type} key={`match_type_${type}`}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} textAlign={"right"} pt={4}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                      <Button variant="outlined" color="secondary" size="modal" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="success" size="modal" disabled={isSubmitting}>
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </ModalBox>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  );
}

export default ToxicityModal;
